import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { uploadToCloudinary } from "@/lib/uploads";
import RichTextEditor from "@/components/RichTextEditor";
import { Plus, Trash2, ExternalLink, X, Image as ImageIcon, BookOpen, GripVertical } from "lucide-react";

const LEVELS = [
  { v: "starter", label: "Starter" },
  { v: "intermediate", label: "Intermediate" },
  { v: "advanced", label: "Advanced" },
];

const slugify = (s) =>
  s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function AdminGuideManager() {
  const [articles, setArticles] = useState(null);
  const [editing, setEditing] = useState(null);
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");
  const [newLevel, setNewLevel] = useState("starter");

  const load = async () => {
    const { data } = await api.get("/guide");
    setArticles(data);
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    if (!articles) return null;
    const g = { starter: [], intermediate: [], advanced: [] };
    for (const a of articles) (g[a.level] || g.starter).push(a);
    return g;
  }, [articles]);

  const create = async () => {
    if (!newTitle || !newSlug) return;
    try {
      const { data } = await api.post("/admin/guide", {
        slug: newSlug, title: newTitle, level: newLevel, body: "", summary: "",
        order: (articles?.filter((a) => a.level === newLevel).length || 0),
        reading_minutes: 5,
      });
      toast.success("Article created");
      setCreatingOpen(false); setNewTitle(""); setNewSlug(""); setNewLevel("starter");
      await load();
      setEditing(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/guide/${a.id}`);
      toast.success("Deleted");
      if (editing?.id === a.id) setEditing(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  // ── Drag-and-drop reordering (within a level group) ──
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  const persistOrder = async (level, ordered) => {
    // Optimistic local update first
    setArticles((prev) => prev.map((a) => {
      if (a.level !== level) return a;
      const idx = ordered.findIndex((o) => o.id === a.id);
      return idx >= 0 ? { ...a, order: idx } : a;
    }));
    try {
      // Only persist articles whose order changed (saves DB hits when nothing shifted)
      const original = articles.filter((a) => a.level === level).sort((x, y) => x.order - y.order);
      const moves = ordered
        .map((a, idx) => ({ id: a.id, oldOrder: original.findIndex((o) => o.id === a.id), newOrder: idx }))
        .filter((m) => m.oldOrder !== m.newOrder);
      await Promise.all(moves.map((m) => api.patch(`/admin/guide/${m.id}`, { order: m.newOrder })));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save new order");
      await load(); // refetch to recover real state
    }
  };

  const handleDrop = (level, targetId) => {
    if (!dragId || dragId === targetId || !articles) return;
    const list = articles
      .filter((a) => a.level === level)
      .sort((x, y) => x.order - y.order);
    const from = list.findIndex((a) => a.id === dragId);
    const to = list.findIndex((a) => a.id === targetId);
    if (from === -1 || to === -1) return;
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setDragId(null);
    setDragOverId(null);
    persistOrder(level, next);
  };

  if (editing) {
    return (
      <ArticleEditor
        key={editing.id}
        article={editing}
        onClose={() => { setEditing(null); load(); }}
      />
    );
  }

  return (
    <div className="mt-4 space-y-4" data-testid="admin-guide-manager">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-stone-500">
          Articles appear under <code className="bg-stone-100 px-1.5 py-0.5 rounded">/app/guide</code> for any signed-in user. <span className="text-stone-400">· Drag the <GripVerticalInline /> handle to reorder.</span>
        </p>
        <Dialog open={creatingOpen} onOpenChange={setCreatingOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="guide-create-open">
              <Plus className="w-4 h-4 mr-1.5" /> New article
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl font-medium">New article</DialogTitle>
              <DialogDescription className="text-sm text-stone-500">
                Fill in the basics — you can edit the body in the WYSIWYG editor right after.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Title</Label>
                <Input
                  value={newTitle}
                  onChange={(e) => { setNewTitle(e.target.value); if (!newSlug) setNewSlug(slugify(e.target.value)); }}
                  data-testid="guide-create-title"
                />
              </div>
              <div>
                <Label>Slug</Label>
                <div className="flex items-center mt-1.5">
                  <span className="text-xs text-stone-400 mr-1">/app/guide/</span>
                  <Input
                    value={newSlug}
                    onChange={(e) => setNewSlug(slugify(e.target.value))}
                    className="font-mono"
                    data-testid="guide-create-slug"
                  />
                </div>
              </div>
              <div>
                <Label>Level</Label>
                <Select value={newLevel} onValueChange={setNewLevel}>
                  <SelectTrigger className="mt-1.5" data-testid="guide-create-level"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={create}
                disabled={!newTitle || !newSlug}
                className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
                data-testid="guide-create-submit"
              >
                Create & edit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {!grouped ? (
        <p className="text-sm text-stone-500">Loading…</p>
      ) : articles.length === 0 ? (
        <p className="text-sm text-stone-500 py-6 text-center">No articles yet. Create your first one above.</p>
      ) : (
        <div className="space-y-5">
          {LEVELS.map((lvl) => grouped[lvl.v].length > 0 && (
            <section key={lvl.v}>
              <div className="text-xs uppercase tracking-[0.2em] text-amber-700 font-semibold mb-2">{lvl.label}</div>
              <ul className="space-y-2">
                {grouped[lvl.v].map((a) => (
                  <li
                    key={a.id}
                    draggable
                    onDragStart={(e) => {
                      setDragId(a.id);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    onDragOver={(e) => {
                      if (!dragId) return;
                      // Only allow dropping within the same level group
                      const dragged = articles.find((x) => x.id === dragId);
                      if (dragged?.level !== lvl.v) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      if (dragOverId !== a.id) setDragOverId(a.id);
                    }}
                    onDragLeave={() => { if (dragOverId === a.id) setDragOverId(null); }}
                    onDrop={(e) => { e.preventDefault(); handleDrop(lvl.v, a.id); }}
                    onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                    className={`flex items-center justify-between gap-3 p-3 border rounded-lg transition-colors ${
                      dragId === a.id ? "opacity-40 border-amber-400 bg-amber-50/60" :
                      dragOverId === a.id ? "border-amber-500 bg-amber-50 ring-2 ring-amber-200" :
                      "border-stone-200 hover:border-amber-300"
                    }`}
                    data-testid={`guide-row-${a.slug}`}
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <GripVertical
                        className="w-4 h-4 text-stone-400 mt-1 shrink-0 cursor-grab active:cursor-grabbing"
                        data-testid={`guide-drag-handle-${a.slug}`}
                        aria-label="Drag to reorder"
                      />
                      <BookOpen className="w-4 h-4 text-stone-400 mt-1 shrink-0" />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{a.title}</div>
                        <div className="text-xs text-stone-500 flex items-center gap-2 flex-wrap mt-0.5">
                          <code className="bg-stone-100 px-1 rounded">{a.slug}</code>
                          <span>{a.reading_minutes} min read</span>
                          <span>· order {a.order}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Link
                        to={`/app/guide/${a.slug}`}
                        target="_blank"
                        className="text-stone-400 hover:text-stone-700"
                        title="View"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Link>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditing(a)}
                        className="rounded-full"
                        data-testid={`guide-edit-${a.slug}`}
                      >
                        Edit
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => remove(a)}
                        className="text-stone-400 hover:text-red-700 h-8 w-8"
                        data-testid={`guide-delete-${a.slug}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function ArticleEditor({ article, onClose }) {
  const [a, setA] = useState(article);
  const [saving, setSaving] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);

  const set = (patch) => setA((cur) => ({ ...cur, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/guide/${a.id}`, {
        slug: a.slug,
        title: a.title,
        summary: a.summary,
        body: a.body,
        level: a.level,
        order: a.order,
        hero_image: a.hero_image,
        reading_minutes: a.reading_minutes,
      });
      setA(data);
      toast.success("Article saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const uploadHero = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadingHero(true);
    try {
      const res = await uploadToCloudinary(f);
      set({ hero_image: res.secure_url });
      toast.success("Hero image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploadingHero(false);
      e.target.value = "";
    }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="guide-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="guide-editor-close">
          <X className="w-4 h-4 mr-1.5" /> Back to list
        </Button>
        <div className="flex items-center gap-2">
          <Link
            to={`/app/guide/${a.slug}`}
            target="_blank"
            className="text-sm text-amber-700 hover:underline inline-flex items-center gap-1"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View live
          </Link>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300"
            data-testid="guide-editor-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <FieldRow label="Title">
          <Input
            value={a.title}
            onChange={(e) => set({ title: e.target.value })}
            data-testid="guide-editor-title"
          />
        </FieldRow>
        <FieldRow label="Slug">
          <Input
            value={a.slug}
            onChange={(e) => set({ slug: slugify(e.target.value) })}
            className="font-mono"
            data-testid="guide-editor-slug"
          />
        </FieldRow>
        <FieldRow label="Level">
          <Select value={a.level} onValueChange={(v) => set({ level: v })}>
            <SelectTrigger data-testid="guide-editor-level"><SelectValue /></SelectTrigger>
            <SelectContent>
              {LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </FieldRow>
        <FieldRow label="Order">
          <Input
            type="number"
            value={a.order}
            onChange={(e) => set({ order: Number(e.target.value || 0) })}
            data-testid="guide-editor-order"
          />
        </FieldRow>
        <FieldRow label="Reading minutes">
          <Input
            type="number"
            value={a.reading_minutes}
            onChange={(e) => set({ reading_minutes: Number(e.target.value || 1) })}
            data-testid="guide-editor-reading-minutes"
          />
        </FieldRow>
        <FieldRow label="Hero image">
          <div className="flex items-center gap-3 flex-wrap">
            {a.hero_image && (
              <img src={a.hero_image} alt="" className="w-20 h-20 rounded-lg object-cover border border-stone-200" />
            )}
            <div className="flex-1 min-w-[180px] space-y-1.5">
              <Input
                value={a.hero_image || ""}
                onChange={(e) => set({ hero_image: e.target.value })}
                placeholder="https://… or upload below"
                data-testid="guide-editor-hero-url"
              />
              <label className="text-xs text-amber-700 hover:underline inline-flex items-center gap-1 cursor-pointer" data-testid="guide-editor-hero-upload-label">
                <ImageIcon className="w-3 h-3" /> {uploadingHero ? "Uploading…" : "Upload"}
                <input type="file" accept="image/*" onChange={uploadHero} className="hidden" />
              </label>
            </div>
          </div>
        </FieldRow>
      </div>

      <FieldRow label="Summary">
        <Textarea
          rows={2}
          value={a.summary || ""}
          onChange={(e) => set({ summary: e.target.value })}
          placeholder="Short tagline shown above the body and in listings."
          data-testid="guide-editor-summary"
        />
      </FieldRow>

      <div>
        <Label className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">Body</Label>
        <div className="mt-1.5">
          <RichTextEditor
            value={a.body || ""}
            onChange={(html) => set({ body: html })}
            placeholder="Write the article. Paste or drop an image to upload it to Cloudinary."
            testidPrefix="guide-editor-body"
          />
        </div>
        <p className="text-xs text-stone-500 mt-2">
          Tip: paste an image directly from your clipboard — it uploads automatically.
        </p>
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}


function GripVerticalInline() {
  return <GripVertical className="w-3 h-3 inline-block align-text-bottom text-stone-400" />;
}
